import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Download, ExternalLink, ShieldCheck, Pill } from "lucide-react";

import {
  hisIntegrationApi,
  hisIntegrationQueryKeys,
  type HisExamDocument,
} from "../../../shared/api/hisIntegration";
import { patientsApi } from "../../../shared/api/patients";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * "Kết quả khám từ HIS (HSBA ký số)" — signed exam-result / prescription PDFs pulled on demand from
 * the external HIS (ADR-0020, EXAM_RESULTS capability). Shown only for a patient with a CCCD or a
 * HIS patient code, and only when the tenant is bound to an external HIS. The PDF (base64) is opened
 * / downloaded straight from the response — nothing is stored in Addy.
 */
export function HisExamResultsCard({
  patientId,
  patientCode,
  hasHis,
}: {
  patientId: string;
  patientCode: string | null | undefined;
  hasHis: boolean;
}) {
  // CCCD (Social_id) is the HIS lookup key — it lives in patient_identifiers, not the base record.
  const identifiers = useQuery({
    queryKey: ["patient", patientId, "identifiers-for-his"],
    queryFn: () => patientsApi.listIdentifiers(patientId),
    enabled: hasHis && !!patientId,
    staleTime: 5 * 60 * 1000,
  });
  const cccd =
    (identifiers.data ?? [])
      .filter((x) => x.identifierType === "CCCD")
      .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))[0]?.value ?? null;

  const key = cccd || patientCode || "";
  const q = useQuery({
    queryKey: hisIntegrationQueryKeys.examResults(key),
    queryFn: () =>
      hisIntegrationApi.examResults({
        socialId: cccd ?? undefined,
        patientCode: patientCode ?? undefined,
        documentType: "ALL",
      }),
    enabled: hasHis && !!key,
    staleTime: 60_000,
  });
  const [openEnc, setOpenEnc] = useState<string | null>(null);

  if (!hasHis || !key) return null;

  const docs = q.data ?? [];

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <FileText aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
        <CardTitle>Kết quả khám từ HIS (HSBA ký số)</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isLoading && <p className="m-0 text-sm text-muted-foreground">Đang tải từ HIS…</p>}
        {q.isError && (
          <p className="m-0 text-sm text-destructive">Không lấy được kết quả khám từ HIS.</p>
        )}
        {!q.isLoading && !q.isError && docs.length === 0 && (
          <p className="m-0 text-sm text-muted-foreground">
            Chưa có tài liệu ký số nào cho bệnh nhân này trên HIS.
          </p>
        )}
        {docs.length > 0 && (
          <ul className="m-0 list-none space-y-2 p-0">
            {docs.map((d, i) => (
              <li
                key={d.documentId ?? i}
                className="rounded-md border border-border p-3"
              >
                <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                    {d.phieuName ?? d.pdfFileName ?? "Tài liệu"}
                    <Badge variant="muted">{categoryLabel(d.category)}</Badge>
                    {d.signed && (
                      <Badge variant="success" className="gap-1">
                        <ShieldCheck aria-hidden="true" className="h-3 w-3" />
                        Đã ký số
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {[d.departmentName, d.doctorName, d.signDate].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {/* Prescription docs can drill into the structured drug list from the HIS. */}
                  {(d.category ?? "").toUpperCase() === "THUOC" &&
                    d.hisPatientCode &&
                    d.encounterCode && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="his-drug-toggle"
                        onClick={() =>
                          setOpenEnc(openEnc === d.encounterCode ? null : d.encounterCode)
                        }
                        title="Xem thuốc"
                      >
                        <Pill aria-hidden="true" className="h-4 w-4" />
                        Thuốc
                      </Button>
                    )}
                  {d.pdfBase64 && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openPdf(d)}
                        title="Xem PDF"
                      >
                        <ExternalLink aria-hidden="true" className="h-4 w-4" />
                        Xem
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadPdf(d)}
                        title="Tải PDF"
                      >
                        <Download aria-hidden="true" className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
                </div>
                {openEnc && openEnc === d.encounterCode && d.hisPatientCode && (
                  <DrugList patientCode={d.hisPatientCode} encounterCode={d.encounterCode} />
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** The structured drug list for one prescription (encounter), fetched from the HIS on expand. */
function DrugList({ patientCode, encounterCode }: { patientCode: string; encounterCode: string }) {
  const q = useQuery({
    queryKey: hisIntegrationQueryKeys.prescription(encounterCode),
    queryFn: () => hisIntegrationApi.prescription(patientCode, encounterCode),
    staleTime: 60_000,
  });
  if (q.isLoading)
    return <p className="mb-0 mt-3 text-sm text-muted-foreground">Đang tải thuốc từ HIS…</p>;
  const drugs = q.data ?? [];
  if (drugs.length === 0)
    return <p className="mb-0 mt-3 text-sm text-muted-foreground">Không có chi tiết thuốc.</p>;
  return (
    <ul className="m-0 mt-3 list-none space-y-1.5 border-t border-border pt-3" data-testid="his-drug-list">
      {drugs.map((dr, i) => (
        <li key={i} className="flex items-start justify-between gap-3 text-sm">
          <span className="font-medium text-foreground">{dr.name ?? "—"}</span>
          <span className="shrink-0 text-right text-xs text-muted-foreground">
            SL {dr.quantity ?? "—"} {dr.unit ?? ""} · {formatDosage(dr.dosage)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** "1^0^1^0" → "Sáng 1 · Chiều 1" (drop the empty slots). */
function formatDosage(dosage: string | null): string {
  if (!dosage) return "";
  const slots = ["Sáng", "Trưa", "Chiều", "Tối"];
  return (
    dosage
      .split("^")
      .map((v, i) => (Number(v) > 0 ? `${slots[i] ?? ""} ${v}` : null))
      .filter(Boolean)
      .join(" · ") || dosage
  );
}

function categoryLabel(c: string | null): string {
  switch ((c ?? "").toUpperCase()) {
    case "THUOC":
      return "Toa thuốc";
    case "CLS":
      return "Cận lâm sàng";
    default:
      return c || "Khác";
  }
}

/** base64 PDF → Blob URL. */
function pdfBlobUrl(d: HisExamDocument): string {
  const bytes = Uint8Array.from(atob(d.pdfBase64 ?? ""), (ch) => ch.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

function openPdf(d: HisExamDocument) {
  window.open(pdfBlobUrl(d), "_blank", "noopener,noreferrer");
}

function downloadPdf(d: HisExamDocument) {
  const a = document.createElement("a");
  a.href = pdfBlobUrl(d);
  a.download = d.pdfFileName || `hsba-${d.documentId ?? "doc"}.pdf`;
  a.click();
}
